import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { supabase } from '../services/supabase';
import api from '../services/api';
import Navbar from '../components/Navbar';
import '../styles/Profile.css';

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

function Profile() {
  const { user, profile, signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [phoneNumber, setPhoneNumber] = useState(profile?.phone_number || '');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const displayName = profile?.full_name || user?.user_metadata?.full_name || 'User';
  const initial = displayName.charAt(0).toUpperCase();

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError('Photo must be under 3MB');
      return;
    }

    setError('');
    setAvatarUploading(true);

    try {
      const filename = `${user.id}/avatar-${Date.now()}.${file.name.split('.').pop()}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filename, file, { contentType: file.type, upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filename);

      await api.patch('/auth/profile', { avatar_url: publicUrl });
      await refreshProfile();
      setSuccess('Photo updated');
    } catch (err) {
      setError('Failed to upload photo');
      console.error(err);
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.patch('/auth/profile', { phone_number: phoneNumber || null });
      await refreshProfile();
      setSuccess('Profile saved');
    } catch (err) {
      setError('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Failed to logout:', err);
    }
  };

  return (
    <div className="page-container">
      <Navbar />

      <div className="profile-container">
        <div className="profile-content">

          <div className="profile-header">
            <div
              className="profile-avatar-wrapper"
              onClick={handleAvatarClick}
              title="Change photo"
            >
              {profile?.avatar_url
                ? <img src={profile.avatar_url} alt={displayName} className="profile-avatar-img" />
                : <div className="profile-avatar">{initial}</div>
              }
              <div className="profile-avatar-overlay">
                {avatarUploading ? '...' : 'Change'}
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />

            <h1>{displayName}</h1>
            <p className="profile-email">{user?.email}</p>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <form onSubmit={handleSave}>
            <div className="profile-section">
              <h2>Contact</h2>
              <div className="settings-list">
                <div className="setting-item">
                  <span>Email</span>
                  <span>{user?.email}</span>
                </div>
                <div className="setting-item setting-item-input">
                  <label htmlFor="phone">Phone number</label>
                  <input
                    id="phone"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
            </div>

            <div className="profile-section">
              <h2>Account</h2>
              <div className="settings-list">
                <div className="setting-item">
                  <span>Member since</span>
                  <span>{new Date(user?.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="profile-actions">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
              <button type="button" onClick={handleLogout} className="btn-logout">
                Log out
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
}

export default Profile;
